import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { StyledTextField } from '@modules/ui-components';
import {
  AdCreativeLocalizedMediaMediaKindEnum,
  type AdCreativeAssetReference,
  type AdCreativeItem,
} from '@segmently/schema-models/ad-creative';
import type {
  AdCreativeCulturalSceneReviewInput,
  AdCreativeCulturalVisualEvidenceView,
  AdCreativeCulturalVisualLibraryView,
} from '../../services/AdCreativeLocalizationApiContract';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';
import { AD_CREATIVE_CULTURAL_VISUAL } from '../../selectors';
import { exactSourceVideoAssetWithGeometry } from './videoLocalizationSource';

const MAX_SCENES = 8;
const MAX_TOTAL_SELECTED_MS = 30_000;
const MAX_SELECTED_RATIO = 0.6;

type CulturalVisualClient = Pick<
  AdCreativeApiClient,
  'listCulturalVisualEvidence' | 'reviewCulturalVisualEvidence'
>;

interface SceneDraft {
  sceneId: string;
  startSeconds: string;
  endSeconds: string;
  instruction: string;
}

interface ProtectedReferenceOption {
  slotKey: string;
  asset: AdCreativeAssetReference & {
    assetId: string;
    original: string;
    sha256: string;
    mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
    width: number;
    height: number;
  };
}

export interface AdCreativeCulturalVisualSelection {
  evidence: AdCreativeCulturalVisualEvidenceView;
}

export interface CulturalVisualReviewPanelProps {
  projectId: string;
  item: AdCreativeItem;
  slotKey: string;
  targetLocale: string;
  client: CulturalVisualClient;
  disabled?: boolean;
  onSelectionChange: (selection: AdCreativeCulturalVisualSelection | null) => void;
}

export function CulturalVisualReviewPanel({
  projectId,
  item,
  slotKey,
  targetLocale,
  client,
  disabled = false,
  onSelectionChange,
}: CulturalVisualReviewPanelProps): React.ReactElement {
  const sourceAsset = useMemo(() => exactSourceVideoAssetWithGeometry(item, slotKey), [item, slotKey]);
  const referenceOptions = useMemo(() => exactSourceImageReferences(item), [item]);
  const [scenes, setScenes] = useState<SceneDraft[]>([emptyScene(1)]);
  const [selectedReferenceSlotKeys, setSelectedReferenceSlotKeys] = useState<string[]>([]);
  const [playbackConfirmed, setPlaybackConfirmed] = useState(false);
  const [referencesConfirmed, setReferencesConfirmed] = useState(false);
  const [evidence, setEvidence] = useState<AdCreativeCulturalVisualEvidenceView | null>(null);
  const [library, setLibrary] = useState<AdCreativeCulturalVisualLibraryView | null>(null);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const identity = `${projectId}:${item.id}:${item.sourceRevision}:${slotKey}:${targetLocale}:${sourceAsset?.assetId ?? ''}:${sourceAsset?.sha256 ?? ''}`;
  const identityRef = useRef(identity);
  const onSelectionChangeRef = useRef(onSelectionChange);
  identityRef.current = identity;
  onSelectionChangeRef.current = onSelectionChange;

  useEffect(() => {
    let active = true;
    setScenes([emptyScene(1)]);
    setSelectedReferenceSlotKeys([]);
    setPlaybackConfirmed(false);
    setReferencesConfirmed(false);
    setEvidence(null);
    setLibrary(null);
    setLibraryLoading(Boolean(sourceAsset));
    setLoadingOlder(false);
    setBusy(false);
    setError(null);
    setStatus(null);
    onSelectionChangeRef.current(null);
    if (sourceAsset) {
      void client.listCulturalVisualEvidence({
        projectId,
        itemId: item.id,
        slotKey,
        sourceRevision: item.sourceRevision,
        targetLocale,
        limit: 10,
      }).then((response) => {
        if (!response.success || !response.data?.library) {
          throw new Error(response.error || 'Saved cultural scene reviews could not be loaded.');
        }
        assertExactLibrary(response.data.library, item, slotKey, targetLocale, sourceAsset);
        if (active && identityRef.current === identity) setLibrary(response.data.library);
      }).catch((nextError) => {
        if (active && identityRef.current === identity) {
          setError(nextError instanceof Error ? nextError.message : String(nextError));
        }
      }).finally(() => {
        if (active && identityRef.current === identity) setLibraryLoading(false);
      });
    }
    return () => { active = false; };
  }, [client, identity, item, projectId, slotKey, sourceAsset, targetLocale]);

  const invalidate = (): void => {
    setEvidence(null);
    setStatus(null);
    setError(null);
    onSelectionChangeRef.current(null);
  };

  const selectExistingEvidence = (selected: AdCreativeCulturalVisualEvidenceView): void => {
    if (!sourceAsset) return;
    assertExactEvidence(selected, item, slotKey, targetLocale, sourceAsset, referenceOptions);
    setScenes(selected.scenes.map((scene) => ({
      sceneId: scene.sceneId,
      startSeconds: formatSeconds(scene.startMs),
      endSeconds: formatSeconds(scene.endMs),
      instruction: scene.instruction,
    })));
    setSelectedReferenceSlotKeys(selected.protectedReferences.map((reference) => reference.slotKey));
    setPlaybackConfirmed(true);
    setReferencesConfirmed(true);
    setEvidence(selected);
    setError(null);
    setStatus('Saved selected-scene review is ready for capability preflight.');
    onSelectionChangeRef.current({ evidence: selected });
  };

  const loadOlder = async (): Promise<void> => {
    if (!sourceAsset || !library?.nextCursor) return;
    const expectedIdentity = identity;
    setLoadingOlder(true);
    setError(null);
    try {
      const response = await client.listCulturalVisualEvidence({
        projectId,
        itemId: item.id,
        slotKey,
        sourceRevision: item.sourceRevision,
        targetLocale,
        limit: 10,
        cursor: library.nextCursor,
      });
      if (!response.success || !response.data?.library) {
        throw new Error(response.error || 'Older cultural scene reviews could not be loaded.');
      }
      assertExactLibrary(response.data.library, item, slotKey, targetLocale, sourceAsset);
      if (identityRef.current !== expectedIdentity) return;
      setLibrary((current) => current ? {
        ...response.data!.library,
        evidence: Array.from(new Map(
          [...current.evidence, ...response.data!.library.evidence]
            .map((entry) => [entry.evidenceId, entry]),
        ).values()),
      } : response.data!.library);
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) {
        setError(nextError instanceof Error ? nextError.message : String(nextError));
      }
    } finally {
      if (identityRef.current === expectedIdentity) setLoadingOlder(false);
    }
  };

  const updateScene = (sceneId: string, field: keyof Omit<SceneDraft, 'sceneId'>, value: string): void => {
    setScenes((current) => current.map((scene) => (
      scene.sceneId === sceneId ? { ...scene, [field]: value } : scene
    )));
    invalidate();
  };

  const addScene = (): void => {
    setScenes((current) => {
      if (current.length >= MAX_SCENES) return current;
      const next = current.reduce((maximum, scene) => (
        Math.max(maximum, Number(scene.sceneId.replace(/^scene_/, '')) || 0)
      ), 0) + 1;
      return [...current, emptyScene(next)];
    });
    invalidate();
  };

  const removeScene = (sceneId: string): void => {
    setScenes((current) => current.length <= 1
      ? current
      : current.filter((scene) => scene.sceneId !== sceneId));
    invalidate();
  };

  const toggleReference = (slot: string, checked: boolean): void => {
    setSelectedReferenceSlotKeys((current) => checked
      ? Array.from(new Set([...current, slot])).sort()
      : current.filter((entry) => entry !== slot));
    setReferencesConfirmed(false);
    invalidate();
  };

  const save = async (): Promise<void> => {
    const expectedIdentity = identity;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      if (!sourceAsset) throw new Error('This slot has no exact trusted source-locale video.');
      if (!playbackConfirmed || !referencesConfirmed) {
        throw new Error('Confirm exact source playback and every protected reference before saving.');
      }
      if (selectedReferenceSlotKeys.length === 0) {
        throw new Error('Select at least one current source image to protect.');
      }
      const normalizedScenes = normalizeScenes(scenes, sourceAsset.durationMs!);
      const response = await client.reviewCulturalVisualEvidence({
        projectId,
        itemId: item.id,
        slotKey,
        sourceRevision: item.sourceRevision,
        targetLocale,
        scenes: normalizedScenes,
        protectedReferenceSlotKeys: selectedReferenceSlotKeys,
        confirmedExactSourcePlayback: true,
        confirmedProtectedReferences: true,
      });
      if (!response.success || !response.data?.evidence) {
        throw new Error(response.error || 'The cultural scene review could not be saved.');
      }
      assertExactEvidence(
        response.data.evidence,
        item,
        slotKey,
        targetLocale,
        sourceAsset,
        referenceOptions,
        normalizedScenes,
        selectedReferenceSlotKeys,
      );
      if (identityRef.current !== expectedIdentity) return;
      setEvidence(response.data.evidence);
      setLibrary((current) => current ? {
        ...current,
        evidence: [
          response.data!.evidence,
          ...current.evidence.filter((entry) => entry.evidenceId !== response.data!.evidence.evidenceId),
        ],
      } : current);
      onSelectionChangeRef.current({ evidence: response.data.evidence });
      setStatus(response.data.reused
        ? 'The existing immutable scene review was verified and reused.'
        : 'Immutable selected-scene review saved from server-resampled frames and current references.');
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) {
        setError(nextError instanceof Error ? nextError.message : String(nextError));
        setEvidence(null);
        onSelectionChangeRef.current(null);
      }
    } finally {
      if (identityRef.current === expectedIdentity) setBusy(false);
    }
  };

  if (!sourceAsset) {
    return (
      <Alert severity="warning" data-testid={AD_CREATIVE_CULTURAL_VISUAL.panel}>
        An exact durable source video is required before cultural scenes can be selected.
      </Alert>
    );
  }

  return (
    <Box
      data-testid={AD_CREATIVE_CULTURAL_VISUAL.panel}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
    >
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Selected cultural scenes</Typography>
          <Typography variant="caption" color="text.secondary">
            Select only the scenes whose setting or cultural context must change. This review uses no AI credits; generation is costed separately.
          </Typography>
        </Box>
        <Box
          component="video"
          controls
          preload="metadata"
          src={sourceAsset.original}
          data-testid={AD_CREATIVE_CULTURAL_VISUAL.sourceVideo}
          sx={{ width: '100%', maxHeight: 360, borderRadius: 1, bgcolor: 'black' }}
        />
        <Alert severity="info">
          At most 8 non-overlapping scenes, 30 seconds total, and 60% of the source duration may be selected. A larger change must become a new creative concept.
        </Alert>
        {libraryLoading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={14} />
            <Typography variant="caption">Loading saved scene reviews…</Typography>
          </Stack>
        )}
        {!libraryLoading && library && library.evidence.length > 0 && (
          <Box
            aria-label="Saved cultural scene reviews"
            data-testid={AD_CREATIVE_CULTURAL_VISUAL.history}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
          >
            <Stack spacing={0.75}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Saved exact reviews</Typography>
              {library.evidence.map((entry) => (
                <Button
                  key={entry.evidenceId}
                  size="small"
                  variant={evidence?.evidenceId === entry.evidenceId ? 'contained' : 'outlined'}
                  disabled={disabled || busy || loadingOlder}
                  data-testid={AD_CREATIVE_CULTURAL_VISUAL.useEvidence(entry.evidenceId)}
                  onClick={() => selectExistingEvidence(entry)}
                >
                  Use {entry.scenes.length}-scene review · {new Date(entry.createdAt).toLocaleString()}
                </Button>
              ))}
              {library.nextCursor && (
                <Button
                  size="small"
                  color="inherit"
                  disabled={disabled || busy || loadingOlder}
                  data-testid={AD_CREATIVE_CULTURAL_VISUAL.loadMore}
                  startIcon={loadingOlder ? <CircularProgress size={12} color="inherit" /> : undefined}
                  onClick={() => void loadOlder()}
                >
                  Load older reviews
                </Button>
              )}
            </Stack>
          </Box>
        )}
        {scenes.map((scene, index) => (
          <Box
            key={scene.sceneId}
            data-testid={AD_CREATIVE_CULTURAL_VISUAL.scene(scene.sceneId)}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
          >
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Scene {index + 1}</Typography>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  disabled={disabled || busy || scenes.length === 1}
                  data-testid={AD_CREATIVE_CULTURAL_VISUAL.removeScene(scene.sceneId)}
                  onClick={() => removeScene(scene.sceneId)}
                >
                  Remove
                </Button>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                <StyledTextField
                  size="small"
                  label="Start (seconds)"
                  value={scene.startSeconds}
                  disabled={disabled || busy}
                  data-testid={AD_CREATIVE_CULTURAL_VISUAL.startSeconds(scene.sceneId)}
                  onChange={(value) => updateScene(scene.sceneId, 'startSeconds', String(value))}
                />
                <StyledTextField
                  size="small"
                  label="End (seconds)"
                  value={scene.endSeconds}
                  disabled={disabled || busy}
                  data-testid={AD_CREATIVE_CULTURAL_VISUAL.endSeconds(scene.sceneId)}
                  onChange={(value) => updateScene(scene.sceneId, 'endSeconds', String(value))}
                />
              </Box>
              <StyledTextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label={`What should change for ${targetLocale}?`}
                value={scene.instruction}
                disabled={disabled || busy}
                helperText="Describe the setting, people, symbols, or behavior to adapt. Do not request a new hook, claim, offer, or product."
                data-testid={AD_CREATIVE_CULTURAL_VISUAL.instruction(scene.sceneId)}
                onChange={(value) => updateScene(scene.sceneId, 'instruction', String(value))}
              />
            </Stack>
          </Box>
        ))}
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon fontSize="small" />}
          disabled={disabled || busy || scenes.length >= MAX_SCENES}
          data-testid={AD_CREATIVE_CULTURAL_VISUAL.addScene}
          onClick={addScene}
        >
          Add selected scene ({scenes.length}/{MAX_SCENES})
        </Button>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
          <Stack spacing={0.75}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Protected product and brand references</Typography>
            <Typography variant="caption" color="text.secondary">
              Segmently resolves these from the current source creative. Browser-provided files, URLs, hashes, and asset IDs are not accepted.
            </Typography>
            {referenceOptions.length === 0 && (
              <Alert severity="warning">Add at least one exact source image slot before using cultural adaptation.</Alert>
            )}
            {referenceOptions.map((option) => (
              <Box key={option.slotKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="img"
                  src={option.asset.original}
                  alt=""
                  loading="lazy"
                  sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 0.75, border: 1, borderColor: 'divider' }}
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedReferenceSlotKeys.includes(option.slotKey)}
                      disabled={disabled || busy}
                      data-testid={AD_CREATIVE_CULTURAL_VISUAL.reference(option.slotKey)}
                      onChange={(event) => toggleReference(option.slotKey, event.target.checked)}
                    />
                  )}
                  label={option.slotKey}
                  sx={{ m: 0, minWidth: 0, '& .MuiFormControlLabel-label': { typography: 'body2', overflowWrap: 'anywhere' } }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={playbackConfirmed}
              disabled={disabled || busy}
              data-testid={AD_CREATIVE_CULTURAL_VISUAL.playbackConfirmation}
              onChange={(event) => {
                setPlaybackConfirmed(event.target.checked);
                invalidate();
              }}
            />
          )}
          label="I played the complete exact source video and checked every selected interval"
          sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { typography: 'caption', pt: 0.4 } }}
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={referencesConfirmed}
              disabled={disabled || busy || selectedReferenceSlotKeys.length === 0}
              data-testid={AD_CREATIVE_CULTURAL_VISUAL.referencesConfirmation}
              onChange={(event) => {
                setReferencesConfirmed(event.target.checked);
                invalidate();
              }}
            />
          )}
          label="I reviewed the selected source images and they contain the exact product, UI, logo, or brand elements that must remain unchanged"
          sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { typography: 'caption', pt: 0.4 } }}
        />
        <Button
          variant="contained"
          size="small"
          disabled={disabled || busy || !playbackConfirmed || !referencesConfirmed || selectedReferenceSlotKeys.length === 0}
          data-testid={AD_CREATIVE_CULTURAL_VISUAL.save}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={() => void save()}
        >
          Save selected-scene review
        </Button>
        {evidence && (
          <Alert severity="success" data-testid={AD_CREATIVE_CULTURAL_VISUAL.evidence}>
            <Stack spacing={0.5}>
              <Typography variant="caption">Immutable evidence is bound to this source video, target language, and current references.</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${evidence.scenes.length} scenes`} />
                <Chip size="small" label={`${evidence.protectedReferences.length} protected references`} />
                <Chip size="small" label={`${evidence.proof.frameCount} proof frames`} />
              </Stack>
            </Stack>
          </Alert>
        )}
        {error && <Alert severity="error" data-testid={AD_CREATIVE_CULTURAL_VISUAL.error}>{error}</Alert>}
        {status && <Alert severity="success">{status}</Alert>}
      </Stack>
    </Box>
  );
}

function emptyScene(ordinal: number): SceneDraft {
  return {
    sceneId: `scene_${String(ordinal).padStart(2, '0')}`,
    startSeconds: '0',
    endSeconds: '1',
    instruction: '',
  };
}

function normalizeScenes(drafts: SceneDraft[], durationMs: number): AdCreativeCulturalSceneReviewInput[] {
  if (drafts.length === 0 || drafts.length > MAX_SCENES) {
    throw new Error(`Select between 1 and ${MAX_SCENES} scenes.`);
  }
  const normalized = drafts.map((draft, index) => {
    const instruction = draft.instruction.trim();
    if (!instruction) throw new Error(`Scene ${index + 1} requires an explicit cultural adaptation instruction.`);
    const startMs = secondsToMs(draft.startSeconds, `Scene ${index + 1} start`);
    const endMs = secondsToMs(draft.endSeconds, `Scene ${index + 1} end`);
    if (endMs <= startMs || endMs > durationMs) {
      throw new Error(`Scene ${index + 1} must have a positive interval inside the source video.`);
    }
    return { sceneId: draft.sceneId, startMs, endMs, instruction };
  }).sort((left, right) => left.startMs - right.startMs || left.sceneId.localeCompare(right.sceneId));
  if (normalized.some((scene, index) => index > 0 && scene.startMs < normalized[index - 1].endMs)) {
    throw new Error('Selected cultural scenes must not overlap.');
  }
  const selectedMs = normalized.reduce((total, scene) => total + scene.endMs - scene.startMs, 0);
  if (selectedMs > MAX_TOTAL_SELECTED_MS || selectedMs / durationMs > MAX_SELECTED_RATIO) {
    throw new Error('Select no more than 30 seconds and 60% of the source video. Larger changes require a new creative.');
  }
  return normalized;
}

function secondsToMs(value: string, field: string): number {
  const seconds = Number(value);
  const milliseconds = Math.round(seconds * 1_000);
  if (!Number.isFinite(seconds) || seconds < 0 || !Number.isSafeInteger(milliseconds)) {
    throw new Error(`${field} must be a valid non-negative number of seconds.`);
  }
  return milliseconds;
}

function formatSeconds(value: number): string {
  return String(Number((value / 1_000).toFixed(3)));
}

function exactSourceImageReferences(item: AdCreativeItem): ProtectedReferenceOption[] {
  const sourceLocale = item.defaultLanguage;
  return Object.entries(item.localizedMedia ?? {}).flatMap(([slotKey, media]) => {
    const entry = media.translations?.[sourceLocale];
    const asset = entry?.disabled ? undefined : entry?.asset;
    if (media.mediaKind !== AdCreativeLocalizedMediaMediaKindEnum.Image
      || !asset?.assetId
      || !asset.original
      || !asset.sha256
      || !['image/jpeg', 'image/png', 'image/webp'].includes(asset.mimeType ?? '')
      || !Number.isSafeInteger(asset.width)
      || !Number.isSafeInteger(asset.height)
      || Number(asset.width) <= 0
      || Number(asset.height) <= 0) return [];
    return [{
      slotKey,
      asset: asset as ProtectedReferenceOption['asset'],
    }];
  }).sort((left, right) => left.slotKey.localeCompare(right.slotKey));
}

function assertExactLibrary(
  library: AdCreativeCulturalVisualLibraryView,
  item: AdCreativeItem,
  slotKey: string,
  targetLocale: string,
  sourceAsset: NonNullable<ReturnType<typeof exactSourceVideoAssetWithGeometry>>,
): void {
  if (library.creativeItemId !== item.id
    || library.slotKey !== slotKey
    || library.sourceRevision !== item.sourceRevision
    || library.targetLocale !== targetLocale
    || library.sourceAssetId !== sourceAsset.assetId
    || library.sourceAssetSha256 !== sourceAsset.sha256
    || !/^[a-f0-9]{64}$/.test(library.sourceComponentHash)) {
    throw new Error('Saved cultural reviews no longer match this exact source video and target language.');
  }
}

function assertExactEvidence(
  evidence: AdCreativeCulturalVisualEvidenceView,
  item: AdCreativeItem,
  slotKey: string,
  targetLocale: string,
  sourceAsset: NonNullable<ReturnType<typeof exactSourceVideoAssetWithGeometry>>,
  referenceOptions: ProtectedReferenceOption[],
  expectedScenes?: AdCreativeCulturalSceneReviewInput[],
  expectedReferenceSlots?: string[],
): void {
  const currentReferences = new Map(referenceOptions.map((option) => [option.slotKey, option.asset]));
  if (evidence.immutable !== true
    || evidence.status !== 'approved'
    || evidence.creativeItemId !== item.id
    || evidence.slotKey !== slotKey
    || evidence.targetLocale !== targetLocale
    || evidence.source.historicalSourceRevision !== item.sourceRevision
    || evidence.source.sourceAssetId !== sourceAsset.assetId
    || evidence.source.sourceSha256 !== sourceAsset.sha256
    || !/^adcultural_[a-f0-9]{40}$/.test(evidence.evidenceId)
    || !/^[a-f0-9]{64}$/.test(evidence.contentHash)
    || !/^[a-f0-9]{64}$/.test(evidence.source.sourceComponentHash)
    || !/^[a-f0-9]{64}$/.test(evidence.proof.sampleSetHash)
    || evidence.proof.frameCount !== evidence.scenes.length
    || evidence.scenes.length < 1
    || evidence.protectedReferences.length < 1
    || evidence.protectedReferences.some((reference) => {
      const asset = currentReferences.get(reference.slotKey);
      return !asset
        || reference.referenceId !== `source_image_slot:${reference.slotKey}`
        || reference.assetId !== asset.assetId
        || reference.sha256 !== asset.sha256
        || reference.mimeType !== asset.mimeType
        || reference.width !== asset.width
        || reference.height !== asset.height;
    })
    || (expectedScenes && JSON.stringify(evidence.scenes.map(({ sampleIds: _sampleIds, ...scene }) => scene))
      !== JSON.stringify(expectedScenes))
    || (expectedReferenceSlots && JSON.stringify(evidence.protectedReferences.map((reference) => reference.slotKey))
      !== JSON.stringify([...expectedReferenceSlots].sort()))) {
    throw new Error('Cultural evidence returned mismatched exact-source lineage, scenes, or references.');
  }
}
